import { defaultLocale } from '@/config/constants';
import { customQueryClient } from '@/config/query-client';
import { useGlobalStore } from '@/stores/global';
import { queryOptions, useQuery } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

type Locale = {
  id: number;
  label: string;
  iso_code: string;
  iso_code_639: string;
  flag: string;
};

async function fetchLocales() {
  const globalStore = useGlobalStore();
  const locales = await fetch(`${globalStore.assets_cdn_url}/static-data/locales.json`);
  const data = (await locales.json()) as Array<Locale>;
  return data;
}

export const localesQueryOptions = queryOptions({
  queryKey: ['locales'],
  queryFn: fetchLocales,
});

export function useLocalesQuery() {
  const { api_url } = storeToRefs(useGlobalStore());
  const enabled = computed(() => !!api_url.value);

  const {
    data: availableLocales,
    suspense: localesSuspense,
    isFetching,
  } = useQuery(
    {
      ...localesQueryOptions,
      enabled,
    },
    customQueryClient,
  );

  function getLocaleById(id: number) {
    const locale = availableLocales.value?.find((locale) => locale.id === id);
    return locale ?? defaultLocale;
  }

  function getLocaleByIsoCode(iso_code: string) {
    const locale = availableLocales.value?.find((locale) => locale.iso_code_639 === iso_code);
    return locale ?? defaultLocale;
  }

  return {
    availableLocales,
    getLocaleById,
    getLocaleByIsoCode,
    localesSuspense,
    isFetching,
  };
}
